import { useCallback, useState } from "react";
import { isADMFormat, isEDDMFormat } from "utils/formats";
import { prepareDesignForEditGQL, PreparedDesignForEdit, PrepareDesignForEditMode } from "../useDesignManagement";
import type { FigmaEditChoice } from "../components/FigmaEditDialog";

const MODE_BY_CHOICE: Record<FigmaEditChoice, PrepareDesignForEditMode> = {
  copy: "COPY",
  flatten: "ORIGINAL",
};

export interface FigmaEditTarget {
  readonly id: string;
  readonly format?: string | null;
  readonly addressBox?: boolean | null;
}

// generate-flyer-pdf paints over the baked box in the rendition, so the live address box is the only one.
export const shouldAutoAddAddressBox = (design: FigmaEditTarget): boolean =>
  Boolean(design.addressBox && design.format && (isADMFormat(design.format) || isEDDMFormat(design.format)));

interface UseFigmaEditChoiceOptions {
  // Runs synchronously inside the click, before the request (used to open a tab without a popup block).
  onStart?: (choice: FigmaEditChoice) => void;
  onPrepared: (prepared: PreparedDesignForEdit, choice: FigmaEditChoice) => void;
  onFailed?: (choice: FigmaEditChoice) => void;
}

export const useFigmaEditChoice = ({ onStart, onPrepared, onFailed }: UseFigmaEditChoiceOptions) => {
  const [busyChoice, setBusyChoice] = useState<FigmaEditChoice | null>(null);
  const [showError, setShowError] = useState(false);

  const choose = useCallback(
    async (design: FigmaEditTarget, choice: FigmaEditChoice) => {
      onStart?.(choice);
      setBusyChoice(choice);
      setShowError(false);
      try {
        const prepared = await prepareDesignForEditGQL(design.id, MODE_BY_CHOICE[choice]);
        setBusyChoice(null);
        onPrepared(prepared, choice);
      } catch (error) {
        console.error(
          `[FigmaEdit] prepareDesignForEdit ${MODE_BY_CHOICE[choice]} failed for design ${design.id}`,
          error
        );
        setBusyChoice(null);
        setShowError(true);
        onFailed?.(choice);
      }
    },
    [onStart, onPrepared, onFailed]
  );

  const reset = useCallback(() => {
    setBusyChoice(null);
    setShowError(false);
  }, []);

  return { busyChoice, showError, choose, reset };
};
